import Userback, {
    getUserback,
    UserbackDestinationType,
    UserbackFeedbackType,
    UserbackWidget,
} from "@userback/widget";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

type ProjectAwareUserbackWidget = Omit<UserbackWidget, "openForm" | "showLauncher"> & {
    openForm: (
        feedbackType?: UserbackFeedbackType,
        destination?: UserbackDestinationType,
        projectKey?: string
    ) => void;
    showLauncher: (projectKey?: string) => void;
};

type LoggingUser = {
    id: string | number;
    email: string;
    name: string;
    role?: string | number;
};

type LoggingParams = {
    user?: LoggingUser;
};

type LoggingContextType = {
    userback: ProjectAwareUserbackWidget | null;
    setLoggingParams: (params: LoggingParams | undefined) => void;
};

type LoggingProviderProps = {
    children: React.ReactNode;
};

const LoggingContext = createContext<LoggingContextType>({
    userback: null,
    setLoggingParams: () => {},
});

export const LoggingProvider = ({ children }: LoggingProviderProps) => {
    const [userback, setUserback] = useState<ProjectAwareUserbackWidget | null>(null);
    const userbackRef = useRef<ProjectAwareUserbackWidget | null>(null);
    const pendingParamsRef = useRef<LoggingParams | undefined>(undefined);
    const isLoadingRef = useRef(false);
    const isMountedRef = useRef(false);

    const showUserbackLauncher = useCallback(
        (userbackWidget: ProjectAwareUserbackWidget, projectKey?: string) => {
            try {
                userbackWidget.showLauncher(projectKey);
            } catch (error) {
                const projectMessage = projectKey ? ` for ${projectKey}` : "";
                console.error(`Failed to show the Userback launcher${projectMessage}:`, error);
            }
        },
        []
    );

    const destroyUserback = useCallback((userbackWidget: UserbackWidget) => {
        try {
            userbackWidget.stopSessionReplay();
        } catch (error) {
            console.error("Failed to stop Userback session replay:", error);
        }

        try {
            userbackWidget.destroy();
        } catch (error) {
            console.error("Failed to destroy Userback:", error);
        }
    }, []);

    const applyLoggingParams = useCallback(
        (userbackWidget: UserbackWidget, params: LoggingParams | undefined) => {
            if (!params?.user) {
                return;
            }

            const { id, email, name, role } = params.user;
            try {
                userbackWidget.identify(String(id), {
                    email,
                    name,
                    ...(role !== undefined ? { role } : {}),
                });
            } catch (error) {
                console.error("Failed to identify the Userback user:", error);
            }
        },
        []
    );

    const initializeUserback = useCallback(async () => {
        if (isLoadingRef.current || !isMountedRef.current) {
            return;
        }

        const token = import.meta.env.VITE_USERBACK_TOKEN;
        const projectKey = import.meta.env.VITE_USERBACK_PROJECT_KEY;
        if (!token || !projectKey) {
            console.error(
                "Userback is not configured. Set VITE_USERBACK_TOKEN and VITE_USERBACK_PROJECT_KEY."
            );
            return;
        }

        isLoadingRef.current = true;

        try {
            const userbackWidget = (await Userback(token, {
                autohide: true,
                on_load: () => {
                    const loadedWidget = getUserback() as ProjectAwareUserbackWidget | undefined;
                    if (loadedWidget) {
                        showUserbackLauncher(loadedWidget);
                    }
                },
            })) as ProjectAwareUserbackWidget;

            if (!isMountedRef.current) {
                destroyUserback(userbackWidget);
                return;
            }

            userbackRef.current = userbackWidget;
            setUserback(userbackWidget);

            showUserbackLauncher(userbackWidget, projectKey);

            try {
                userbackWidget.startSessionReplay({});
            } catch (error) {
                console.error("Failed to start Userback session replay:", error);
            }

            applyLoggingParams(userbackWidget, pendingParamsRef.current);
        } catch (error) {
            console.error("Failed to initialize Userback:", error);
        } finally {
            isLoadingRef.current = false;
        }
    }, [applyLoggingParams, destroyUserback, showUserbackLauncher]);

    useEffect(() => {
        isMountedRef.current = true;
        void initializeUserback();

        return () => {
            isMountedRef.current = false;
            if (userbackRef.current) {
                destroyUserback(userbackRef.current);
            }
            userbackRef.current = null;
        };
    }, [destroyUserback, initializeUserback]);

    const handleSetLoggingParams = useCallback(
        (params: LoggingParams | undefined) => {
            pendingParamsRef.current = params;

            const userbackWidget = userbackRef.current;
            if (!userbackWidget) {
                return;
            }

            if (params?.user) {
                applyLoggingParams(userbackWidget, params);
                return;
            }

            destroyUserback(userbackWidget);
            userbackRef.current = null;
            setUserback(null);
            void initializeUserback();
        },
        [applyLoggingParams, destroyUserback, initializeUserback]
    );

    return (
        <LoggingContext.Provider value={{ userback, setLoggingParams: handleSetLoggingParams }}>
            {children}
        </LoggingContext.Provider>
    );
};

export const useLogging = () => useContext(LoggingContext);
